import styled from 'styled-components';
import { type TemplateType } from '@cls-nordic/pdf-templates/browser';
import { PdfPreview } from '@cls-nordic/pdf-templates/preview';

import { CertificatePreviewInput } from './certificatePreviewInput';
import { JourneyCertificatePreviewSkeleton } from './JourneyCertificatePreviewSkeleton';

const PreviewCanvasStyled = styled.div`
  ${({ theme }) => theme.shadow.primary[32]}
  overflow: hidden;
  line-height: 0;
`;

const StateTextStyled = styled.p`
  margin: 0;
  ${({ theme }) => theme.typography.body1};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

type JourneyCertificatePreviewProps = {
  loading: boolean;
  error: boolean;
  payload: CertificatePreviewInput | null;
  templateId: TemplateType;
  /** Width (px) the A4 page should render at; 0 until the container has been measured. */
  width: number;
};

export const JourneyCertificatePreview = ({
  loading,
  error,
  payload,
  templateId,
  width,
}: JourneyCertificatePreviewProps): JSX.Element | null => {
  if (error) {
    return <StateTextStyled>Unable to load certificate.</StateTextStyled>;
  }

  if (!loading && !payload) {
    return <StateTextStyled>Certificate is not available yet.</StateTextStyled>;
  }

  // Wait until the container has been measured so the A4 page fits its frame exactly.
  if (width === 0) {
    return null;
  }

  const isPdfReady = !loading && payload !== null;

  return (
    <PreviewCanvasStyled>
      {isPdfReady ? (
        <PdfPreview
          templateId={templateId}
          input={payload}
          width={width}
          renderLoading={() => <JourneyCertificatePreviewSkeleton width={width} />}
        />
      ) : (
        <JourneyCertificatePreviewSkeleton width={width} />
      )}
    </PreviewCanvasStyled>
  );
};
